<script lang="ts">
  import { thumbCache } from '../ink/thumbnail';
  import type { Page } from '../store/types';
  import type { Repo } from '../store/repo';
  import type { Theme } from '../theme/tokens';

  let { pages, currentPageId, theme, repo, onpick, onclose }: {
    pages: Page[];
    currentPageId: string;
    theme: Theme;
    repo: Repo;
    onpick: (pageId: string) => void;
    onclose: () => void;
  } = $props();

  const THUMB_MAX_W = 200;
  const THUMB_MAX_H = 280;

  let scroller: HTMLDivElement;
  // Double-render guard: page ids whose thumbnail has already been kicked off.
  const started = new Set<string>();

  async function renderThumbInto(el: HTMLElement, page: Page) {
    if (started.has(page.id)) return;
    started.add(page.id);
    // Re-fetch the page record: the prop's `rev` was captured at page load, but
    // strokes drawn since then bumped it — the cache key must see the live rev.
    const [fresh, strokes, textBoxes] = await Promise.all([
      repo.getPage(page.id),
      repo.getActiveStrokes(page.id),
      repo.getActiveTextBoxes(page.id),
    ]);
    const canvas = thumbCache.getOrRender(fresh ?? page, strokes, textBoxes, theme, THUMB_MAX_W, THUMB_MAX_H);
    el.replaceChildren(canvas); // swap placeholder for the rendered thumbnail
  }

  // Lazy thumbnails: only cards scrolled into view render. Feature-guarded the
  // same way engine.ts guards ResizeObserver — environments without
  // IntersectionObserver (jsdom) render everything immediately.
  $effect(() => {
    const list = pages; // read synchronously so the effect tracks the prop
    const cards = Array.from(scroller.querySelectorAll<HTMLElement>('.thumb'));
    const pageFor = (el: HTMLElement) => list[Number(el.dataset.index)];
    if (typeof IntersectionObserver === 'undefined') {
      for (const el of cards) {
        const p = pageFor(el);
        if (p) void renderThumbInto(el, p);
      }
      return;
    }
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        io.unobserve(entry.target); // render at most once per card
        const el = entry.target as HTMLElement;
        const p = pageFor(el);
        if (p) void renderThumbInto(el, p);
      }
    }, { root: scroller, rootMargin: '120px' });
    for (const el of cards) io.observe(el);
    return () => io.disconnect();
  });
</script>

<div class="scrim" onclick={onclose} role="presentation"></div>
<div class="pages-drawer" bind:this={scroller} role="dialog" aria-label="Pages">
  {#each pages as p, i (p.id)}
    <button class="page-card" class:current={p.id === currentPageId} onclick={() => onpick(p.id)}>
      <div
        class="thumb"
        data-index={i}
        style="aspect-ratio: {p.kind === 'fixed' ? '210 / 297' : '4 / 3'}"
      ></div>
      <span class="num">{i + 1}</span>
    </button>
  {/each}
</div>

<style>
  .scrim { position: fixed; inset: 0; z-index: 30; background: rgba(0, 0, 0, 0.2); }
  .pages-drawer {
    position: fixed; top: 0; bottom: 0; left: 0; width: 260px; z-index: 31;
    background: var(--surface); box-shadow: var(--shadow-soft);
    overflow-y: auto; overscroll-behavior: contain;
    padding: max(12px, env(safe-area-inset-top)) 12px 12px;
    display: flex; flex-direction: column; gap: 12px;
  }
  .page-card {
    border: none; background: transparent; color: var(--ink-primary);
    padding: 8px; border-radius: var(--radius-panel); cursor: pointer;
    display: flex; flex-direction: column; align-items: center; gap: 6px;
  }
  .page-card.current .thumb { box-shadow: 0 0 0 2px var(--accent); border-color: var(--accent); }
  .thumb {
    width: 200px; background: var(--paper);
    border: 1px solid var(--hairline); border-radius: 6px;
    display: grid; place-items: center; overflow: hidden;
  }
  .thumb :global(canvas) { display: block; max-width: 100%; max-height: 100%; }
  .num { font-size: 12px; opacity: 0.7; }
</style>
